import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import { ACCOUNT_ROLE_GROUPS, type Account } from '@fsp/core';
import { api, ApiError, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, PageHead, PanelShell } from '../layout';

interface Role { role: string; group: keyof typeof ACCOUNT_ROLE_GROUPS; label: string; classes: string; default: string; code: string; problem: string | null }
interface Plan { source: string; accounts: Account[]; roles: Role[] }

const CLASS_NAMES: Record<string, string> = { 1: 'Ilgalaikis turtas', 2: 'Trumpalaikis turtas', 3: 'Nuosavas kapitalas', 4: 'Įsipareigojimai', 5: 'Pajamos', 6: 'Sąnaudos' };

/**
 * Sąskaitų planas: the sample chart of accounts with the company's changes (sub-accounts, names, hidden
 * accounts) and the account each kind of operation goes to.
 */
export function AccountsPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: ['accounts'], queryFn: () => api.get<Plan>('api/v1/accounts') });
  const readOnly = me.company.role === 'viewer' || Boolean(me.viewAsAdmin);
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [editing, setEditing] = useState<{ code: string; mode: 'add' | 'rename' } | null>(null);
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: async () => { setEditing(null); setRoles({}); await queryClient.invalidateQueries({ queryKey: ['accounts'] }); },
  });
  const plan = data.data;
  const postable = useMemo(() => (plan?.accounts ?? []).filter((a) => a.postable && !a.hidden), [plan]);
  const byCode = useMemo(() => new Map((plan?.accounts ?? []).map((a) => [a.code, a])), [plan]);
  const q = search.trim().toLowerCase();
  const shown = (plan?.accounts ?? []).filter((a) => (showHidden || !a.hidden) && (!q || a.code.startsWith(q) || a.name.toLowerCase().includes(q)));
  const changed = Object.entries(roles).filter(([role, code]) => plan?.roles.find((r) => r.role === role)?.code !== code);

  const submit = (fn: (v: FormData) => Promise<unknown>) => (e: FormEvent<HTMLFormElement>) => { e.preventDefault(); const v = new FormData(e.currentTarget); act.mutate(() => fn(v)); };
  const str = (v: FormData, k: string) => String(v.get(k) ?? '').trim();

  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Buhalterija')} title={t('Sąskaitų planas')} text={t('Pavyzdinis sąskaitų planas su jūsų pakeitimais ir sąskaitos, į kurias registruojamos operacijos.')} />}>
      {data.isError ? <Alert kind="error">{errorText(t, data.error)}</Alert> : null}
      {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}

      {plan ? <>
        <section className="panel-card">
          <div className="card-head"><div><p className="eyebrow">{t('Susiejimai')}</p><h2>{t('Operacijų sąskaitos')}</h2></div></div>
          <p className="muted">{t('Į šias sąskaitas programa registruos sąskaitas faktūras, banko ir kasos operacijas bei darbo užmokestį. Numatytosios — iš pavyzdinio plano; buhalteris gali pasirinkti kitas (pvz., atskirą subsąskaitą kiekvienam bankui).')}</p>
          <div className="account-roles">
            {(Object.keys(ACCOUNT_ROLE_GROUPS) as (keyof typeof ACCOUNT_ROLE_GROUPS)[]).map((group) => (
              <fieldset key={group}>
                <legend>{t(ACCOUNT_ROLE_GROUPS[group])}</legend>
                {plan.roles.filter((r) => r.group === group).map((r) => {
                  const value = roles[r.role] ?? r.code;
                  const current = byCode.get(r.code);
                  const options = postable.filter((a) => r.classes.includes(a.code[0]!));
                  return (
                    <label key={r.role}>
                      <span>{t(r.label)}</span>
                      <select value={value} disabled={readOnly} onChange={(e) => setRoles({ ...roles, [r.role]: e.target.value })}>
                        {options.some((a) => a.code === r.code) ? null : <option value={r.code}>{r.code} {current?.name ?? ''}</option>}
                        {options.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                      </select>
                      {r.problem && !roles[r.role] ? <small className="account-problem">{errorText(t, new ApiError(400, r.problem))}</small> : null}
                    </label>
                  );
                })}
              </fieldset>
            ))}
          </div>
          {readOnly ? null : (
            <div className="form-actions">
              <button type="button" className="button button--primary" disabled={!changed.length || act.isPending} onClick={() => act.mutate(() => api.put('api/v1/accounts/roles', { roles: Object.fromEntries(changed) }))}>{t('Išsaugoti')}</button>
              {changed.length ? <button type="button" className="button button--ghost" onClick={() => setRoles({})}>{t('Atšaukti')}</button> : null}
            </div>
          )}
        </section>

        <BankAccounts readOnly={readOnly} postable={postable} />

        <section className="panel-card">
          <div className="card-head">
            <div><p className="eyebrow">{plan.source}</p><h2>{t('Sąskaitos')}</h2></div>
            <div className="form-actions">
              <input type="search" className="account-search" placeholder={t('Kodas arba pavadinimas')} aria-label={t('Paieška')} value={search} onChange={(e) => setSearch(e.target.value)} />
              <label className="check"><input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />{t('Rodyti paslėptas')}</label>
            </div>
          </div>
          <p className="muted">{t('Pavyzdinio plano sąskaitas galima pervadinti arba paslėpti; savo subsąskaitas — pridėti ir ištrinti. Operacijos registruojamos tik į sąskaitas be subsąskaitų.')}</p>
          <table className="account-plan">
            <tbody>
              {shown.map((a) => (
                <tr key={a.code} className={[a.level === 0 ? 'is-class' : '', a.hidden ? 'is-hidden' : '', a.postable ? '' : 'is-group'].join(' ')}>
                  <td className="account-code" style={{ paddingLeft: `${8 + a.level * 14}px` }}>{a.code}</td>
                  <td>
                    {editing?.code === a.code && editing.mode === 'rename' ? (
                      <form className="account-inline" onSubmit={submit((v) => api.patch(`api/v1/accounts/${a.code}`, { name: str(v, 'name') }))}>
                        <input name="name" defaultValue={a.name} maxLength={200} required autoFocus />
                        <button className="button button--primary button--small" disabled={act.isPending}>{t('Išsaugoti')}</button>
                        {a.standard ? <button type="button" className="text-link" onClick={() => act.mutate(() => api.patch(`api/v1/accounts/${a.code}`, { name: null }))}>{t('Pavyzdinis pavadinimas')}</button> : null}
                        <button type="button" className="text-link" onClick={() => setEditing(null)}>{t('Atšaukti')}</button>
                      </form>
                    ) : <>{a.level === 0 ? t(CLASS_NAMES[a.code] ?? a.name) : a.name}{a.standard ? null : <span className="shop-tag">{t('Sava')}</span>}{a.hidden ? <span className="shop-tag">{t('Paslėpta')}</span> : null}</>}
                    {editing?.code === a.code && editing.mode === 'add' ? (
                      <form className="account-inline" onSubmit={submit((v) => api.post('api/v1/accounts', { code: str(v, 'code'), name: str(v, 'name') }))}>
                        <input name="code" defaultValue={a.code} inputMode="numeric" pattern="[1-6][0-9]{1,7}" maxLength={8} required aria-label={t('Kodas')} className="account-code-input" autoFocus />
                        <input name="name" placeholder={t('Pavadinimas')} maxLength={200} required />
                        <button className="button button--primary button--small" disabled={act.isPending}>{t('Pridėti')}</button>
                        <button type="button" className="text-link" onClick={() => setEditing(null)}>{t('Atšaukti')}</button>
                      </form>
                    ) : null}
                  </td>
                  <td className="account-actions">
                    {readOnly || a.level === 0 || editing?.code === a.code ? null : <>
                      <button type="button" className="text-link" onClick={() => setEditing({ code: a.code, mode: 'add' })}>+ {t('Subsąskaita')}</button>
                      <button type="button" className="text-link" onClick={() => setEditing({ code: a.code, mode: 'rename' })}>{t('Pervadinti')}</button>
                      {a.standard
                        ? <button type="button" className="text-link" onClick={() => act.mutate(() => api.patch(`api/v1/accounts/${a.code}`, { hidden: !a.hidden }))}>{a.hidden ? t('Rodyti') : t('Slėpti')}</button>
                        : <button type="button" className="text-link" onClick={() => act.mutate(() => api.delete(`api/v1/accounts/${a.code}`))}>{t('Ištrinti')}</button>}
                    </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </> : null}
    </PanelShell>
  );
}

/** Each bank account's own sub-account (e.g. 2711 Swedbank under 271); without one, the bank account above. */
function BankAccounts({ readOnly, postable }: { readOnly: boolean; postable: Account[] }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: ['books', 'bank-accounts'], queryFn: () => api.get<{ accounts: { id: number; iban: string; label: string; currency: string; account: string | null }[] }>('api/v1/books/bank-accounts') });
  const save = useMutation({
    mutationFn: ({ id, account }: { id: number; account: string | null }) => api.put(`api/v1/books/bank-accounts/${id}`, { account }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['books', 'bank-accounts'] }),
  });
  const banks = data.data?.accounts ?? [];
  if (!banks.length) return null;
  const options = postable.filter((a) => a.code.startsWith('27'));
  return (
    <section className="panel-card">
      <div className="card-head"><div><p className="eyebrow">{t('Pinigai')}</p><h2>{t('Banko sąskaitos')}</h2></div></div>
      <p className="muted">{t('Jei kiekvienam bankui sukūrėte subsąskaitą (pvz., 2711 Swedbank), pasirinkite ją čia. Pervedimai tarp savų sąskaitų registruojami per „Pinigai kelyje“.')}</p>
      {save.isError ? <Alert kind="error">{errorText(t, save.error)}</Alert> : null}
      <div className="account-roles">
        <fieldset>
          {banks.map((b) => (
            <label key={b.id}>
              <span>{b.label || b.iban}{b.label ? ` · ${b.iban}` : ''}{b.currency !== 'EUR' ? ` · ${b.currency}` : ''}</span>
              <select value={b.account ?? ''} disabled={readOnly || save.isPending} onChange={(e) => save.mutate({ id: b.id, account: e.target.value || null })}>
                <option value="">{t('Kaip „Banko sąskaita“ aukščiau')}</option>
                {options.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
              </select>
            </label>
          ))}
        </fieldset>
      </div>
    </section>
  );
}
